import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommonPagination from '../../common/Pagination';
import type {
  PhysicalCountDetailGroupKey,
  PhysicalCountDetailItem,
} from '../../../types/physicalCount.types';
import type { GroupUiState } from './PhysicalCountDetailModal.hooks';
import type { PhysicalCountGroupAccent } from './physicalCountDetailGroups';
import { INV_AVAILABILITY_WRITE_OFF_LABEL } from '../../../constants/inventory-status-labels';
import { formatDateTime } from '../../../utils/formatters/date';

const formatDisplayDateTime = (dateStr?: string) => {
  if (dateStr == null || dateStr === '') return '-';
  return formatDateTime(dateStr) || '-';
};

const accentToColor = (accent: PhysicalCountGroupAccent, theme: Theme) => {
  switch (accent) {
    case 'success':
      return theme.palette.success.main;
    case 'error':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

export interface PhysicalCountDetailGroupProps {
  groupKey: PhysicalCountDetailGroupKey;
  label: string;
  count: number;
  accent: PhysicalCountGroupAccent;
  helperText?: string;
  state: GroupUiState;
  disabled?: boolean;
  onToggle: () => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (event: SelectChangeEvent<unknown>) => void;
}

const PhysicalCountDetailGroup: React.FC<PhysicalCountDetailGroupProps> = ({
  groupKey,
  label,
  count,
  accent,
  helperText,
  state,
  disabled = false,
  onToggle,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const theme = useTheme();
  const accentColor = accentToColor(accent, theme);

  const tableContainerSx = {
    borderRadius: 0,
    bgcolor: 'background.paper',
    borderColor: 'divider',
    backgroundImage: 'none',
    boxShadow: 'none',
    '& .MuiTableHead-root .MuiTableCell-root': {
      bgcolor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.06)
          : theme.palette.grey[100],
      color: 'text.secondary',
      fontWeight: 600,
      fontSize: '0.8125rem',
      borderBottomColor: 'divider',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      color: 'text.primary',
      borderBottomColor: 'divider',
      fontSize: '0.875rem',
    },
    '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
      borderBottom: 'none',
    },
  };

  const renderRow = (row: PhysicalCountDetailItem) => {
    if (groupKey === 'duplicate') {
      return (
        <TableRow key={row.scannedCode ?? row.productUniqueCode}>
          <TableCell>{row.scannedCode ?? row.productUniqueCode ?? '-'}</TableCell>
          <TableCell align="right">{row.timesScanned ?? '-'}</TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={row.id ?? row.productUniqueCode ?? row.productCode}>
        <TableCell>{row.productUniqueCode ?? '-'}</TableCell>
        <TableCell>{row.productCode ?? '-'}</TableCell>
        <TableCell>{row.productName ?? '-'}</TableCell>
        <TableCell align="right">{row.expectedQty ?? '-'}</TableCell>
        <TableCell align="right">{row.actualQty ?? '-'}</TableCell>
        {groupKey === 'scanned_not_available' && (
          <TableCell>
            {row.availabilityStatus === 'CANCELLED'
              ? INV_AVAILABILITY_WRITE_OFF_LABEL
              : (row.availabilityStatus ?? '-')}
          </TableCell>
        )}
        {groupKey === 'missing' && (
          <TableCell>
            {row.writtenOffAt ? (
              <Typography variant="caption" color="text.secondary">
                {formatDisplayDateTime(row.writtenOffAt)}
                {row.writtenOffBy ? ` · ${row.writtenOffBy}` : ''}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Pendiente
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
    );
  };

  const renderTableHead = () => {
    if (groupKey === 'duplicate') {
      return (
        <TableRow>
          <TableCell>Código único</TableCell>
          <TableCell align="right">Veces escaneado</TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow>
        <TableCell>Código único</TableCell>
        <TableCell>Código</TableCell>
        <TableCell>Producto</TableCell>
        <TableCell align="right">Esperado</TableCell>
        <TableCell align="right">Escaneado</TableCell>
        {groupKey === 'scanned_not_available' && <TableCell>Estado</TableCell>}
        {groupKey === 'missing' && <TableCell>Baja en inventario</TableCell>}
      </TableRow>
    );
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1.5,
        overflow: 'hidden',
        opacity: disabled ? 0.55 : 1,
        bgcolor: 'background.paper',
        borderLeft: `3px solid ${accentColor}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.06)
              : theme.palette.grey[50],
          borderBottom: state.expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': disabled
            ? undefined
            : {
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.1)
                    : theme.palette.grey[100],
              },
        }}
        onClick={disabled ? undefined : onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          {state.expanded ? (
            <ExpandLessIcon sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
          )}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: accentColor }}>
              {label} ({count})
            </Typography>
            {helperText && !state.expanded && (
              <Typography variant="caption" color="text.secondary" display="block">
                {helperText}
              </Typography>
            )}
          </Box>
        </Box>
        {!disabled && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
          >
            {state.expanded ? 'Ocultar' : 'Ver'} detalles
          </Button>
        )}
      </Box>

      <Collapse in={state.expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 0 }}>
          {helperText && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ px: 2, pt: 1.5 }}
            >
              {helperText}
            </Typography>
          )}

          {state.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : state.error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {state.error}
            </Alert>
          ) : state.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No hay registros en esta categoría.
            </Typography>
          ) : (
            <>
              <TableContainer sx={tableContainerSx}>
                <Table size="small">
                  <TableHead>{renderTableHead()}</TableHead>
                  <TableBody>{state.items.map(renderRow)}</TableBody>
                </Table>
              </TableContainer>

              {state.total > 0 && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CommonPagination
                    currentPage={state.page}
                    itemsPerPage={state.itemsPerPage}
                    totalItems={state.total}
                    onPageChange={onPageChange}
                    onItemsPerPageChange={onItemsPerPageChange}
                    itemLabelPlural="registros"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PhysicalCountDetailGroup;
